import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import {
  TimesheetOvertimeRequestFragment,
  UpdateTimesheetOvertimeRequestDocument,
  UpdateTimesheetOvertimeRequestMutationVariables,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, FormProvider } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { EventTypes, dispatch } from 'modules/core/lib/pubSub'
import app from 'modules/core/store/models/app'

import ApprovalsInformationBlock from 'modules/approvals/components/ApprovalsInformationBlock'
import OvertimeRequestForm from 'modules/attendance/components/ClockIn/Modals/OvertimeRequest/Form'

import ModalFooter from './ModalFooter'

interface Props {
  onClose: () => void
  overtimeRequest: TimesheetOvertimeRequestFragment
}

const EditOvertimeRequest = ({ onClose, overtimeRequest }: Props) => {
  const defaultValues: UpdateTimesheetOvertimeRequestMutationVariables = {
    id: overtimeRequest.id,
    date: overtimeRequest.date,
    hoursAmount: overtimeRequest.hoursAmountInCents / 100,
    description: overtimeRequest.description,
    reason: overtimeRequest.reason,
  }

  const [updateOvertimeRequest, { loading: updating }] = useMutation(
    UpdateTimesheetOvertimeRequestDocument,
    {
      extractorFn: (data) => data.attendanceMutations.updateAttendanceOvertimeRequest,
    }
  )

  const { employee: currentEmployee } = useCurrentSession()

  const canApprove = !!overtimeRequest.approver

  const isAuthor = currentEmployee?.id === overtimeRequest.employeeId
  const isPending = overtimeRequest.status === 'pending'
  const isApproved = overtimeRequest.status === 'approved'
  const isRejected = overtimeRequest.status === 'rejected'
  const submitting = updating

  let title = i18n.t('time_tracking.modals.overtime_request.general_title')
  let description = i18n.t('time_tracking.modals.overtime_request.general_pending_description')

  if (isAuthor) {
    title = i18n.t('time_tracking.modals.overtime_request.modify_title')

    if (isPending) {
      description = i18n.t('time_tracking.modals.overtime_request.modify_description')
    } else if (isApproved) {
      description = i18n.t(
        'time_tracking.modals.overtime_request.approved_and_editable_employee_description'
      )
    } else {
      description = i18n.t(
        'time_tracking.modals.overtime_request.rejected_and_editable_employee_description'
      )
    }
  } else if (canApprove) {
    title = i18n.t('time_tracking.modals.overtime_request.review_title')
    description = i18n.t('time_tracking.modals.overtime_request.review_description')
  } else {
    if (isApproved) {
      description = i18n.t('time_tracking.modals.overtime_request.general_approved_description')
    } else if (isRejected) {
      description = i18n.t('time_tracking.modals.overtime_request.general_rejected_description')
    }
  }

  return (
    <FormProvider
      defaultValues={defaultValues}
      validate={(data) => {
        const errors: FormErrors<UpdateTimesheetOvertimeRequestMutationVariables> = {}

        if (!data.date) {
          errors.date = i18n.t('common.empty')
        }
        if (data.hoursAmount && data.hoursAmount <= 0) {
          errors.hoursAmount = i18n.t('common.empty')
        }

        return errors
      }}
      onSubmit={async (data, { setErrors }) => {
        const result = await updateOvertimeRequest({
          variables: {
            id: data.id,
            date: data.date,
            hoursAmount: data.hoursAmount,
            description: data.description,
            reason: data.reason,
          },
        })

        const handled = handleResult(
          result,
          ({
            description: descriptionResult,
            date: dateResult,
            hoursAmount: hoursAmountResult,
          }) => {
            setErrors({
              description: descriptionResult,
              date: dateResult,
              hoursAmount: hoursAmountResult,
            })
            app.setFlash({
              type: 'wrong',
              message: i18n.t('posts.communities.error_message'),
            })
          }
        )
        if (handled.success && handled.data.overtimeRequest) {
          dispatch({ type: EventTypes.ATTENDANCE_SHIFT_TOGGLED })
          onClose()
          app.setFlash({
            type: 'correct',
            message: i18n.t('common.saved_successfully'),
          })
        }

        return handled
      }}
    >
      <Modal
        illustration="timer"
        title={title}
        description={description}
        onClose={onClose}
        footer={
          <ModalFooter
            canApprove={canApprove}
            isApprover={canApprove}
            isAuthor={isAuthor}
            submitting={submitting}
            handleClose={onClose}
          />
        }
      >
        <Box gap="s24">
          <OvertimeRequestForm
            canApprove={canApprove}
            isEditing
            isPending={isPending}
            employeeId={overtimeRequest.employeeId}
          />
          <ApprovalsInformationBlock
            resourceId={overtimeRequest.id}
            resourceType="Attendance::OvertimeRequest"
          />
        </Box>
      </Modal>
    </FormProvider>
  )
}

export default observer(EditOvertimeRequest)
